import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Building2, Mail, Phone, Globe, User, Loader2, CheckCircle2, Compass, Smartphone, AlertTriangle, Copy, MapPin, MailX, Truck, ExternalLink, Landmark } from "lucide-react";
import { toast } from "sonner";
import { copyContact } from "@/lib/copyContact";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const emptyContact = () => ({ _uid: uid(), name: "", title: "", email: "", phone: "", phone_mobile: "" });
const emptyAddress = () => ({ _uid: uid(), id: "", label: "", text: "", hoo: "", del_pu: "", delivery_instructions: "" });
const emptyEntry = () => ({ company_name: "", address: "", addresses: [emptyAddress()], website: "", portal_url: "", notes: "", contacts: [emptyContact()], tags: [], status: "active", region: "", abbreviation: "", bank_name: "", bank_routing: "", bank_account: "" });

const REGIONS = ["US", "Europe", "UK"];

const DIRECTORIES = [
  { key: "suppliers", label: "Suppliers", endpoint: "/suppliers" },
  { key: "co-packers", label: "Co-Packers", endpoint: "/co-packers" },
  { key: "customers", label: "Customers", endpoint: "/customers" },
  { key: "distributors", label: "Distributors", endpoint: "/distributors" },
  { key: "land-freight", label: "Land Freight", endpoint: "/land-freight" },
  { key: "sea-freight", label: "Sea Freight", endpoint: "/sea-freight" },
];
const webHref = (w) => (w?.startsWith("http") ? w : `https://${w}`);

const StatusBadge = ({ status }) => (
  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${status === "prospective" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
    {status === "prospective" ? "Prospective" : "Active"}
  </span>
);

function EntryDialog({ open, onOpenChange, initial, onSaved, onMoved, endpoint, prefix, singular, roleLabel, supplierMode, regionMode = false, entries = [] }) {
  const [form, setForm] = useState(emptyEntry());
  const [saving, setSaving] = useState(false);
  const [catOptions, setCatOptions] = useState([]);
  const [dupConfirmed, setDupConfirmed] = useState(false);
  const [targetEndpoint, setTargetEndpoint] = useState(endpoint);
  const targetIsSupplier = targetEndpoint === "/suppliers";
  const showSupplierFields = supplierMode || targetIsSupplier;
  const showRegionField = regionMode || targetEndpoint === "/customers";

  useEffect(() => {
    if (!open || !showSupplierFields) return;
    api.get("/components")
      .then((r) => setCatOptions(Array.from(new Set((r.data || []).map((c) => c.name))).sort()))
      .catch(() => {});
  }, [open, showSupplierFields]);

  const toggleTag = (t) => setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  useEffect(() => {
    if (!open) return;
    const seedAddresses = (init) => {
      const list = init.addresses?.length ? init.addresses : init.address ? [{ id: "", label: "", text: init.address }] : [];
      const seeded = list.map((a) => ({ ...emptyAddress(), ...a, _uid: uid() }));
      return seeded.length ? seeded : [emptyAddress()];
    };
    setForm(initial
      ? { ...emptyEntry(), ...initial, addresses: seedAddresses(initial), contacts: initial.contacts?.length ? initial.contacts.map((c) => ({ ...emptyContact(), ...c })) : [emptyContact()] }
      : emptyEntry());
    setDupConfirmed(false);
    setTargetEndpoint(endpoint);
  }, [open, initial, endpoint]);

  // Duplicate detection (case-insensitive) against the rest of the directory
  const norm = (s) => (s || "").trim().toLowerCase();
  const companyDup = useMemo(() => {
    const n = norm(form.company_name);
    if (!n) return null;
    return entries.find((e) => e.id !== initial?.id && norm(e.company_name) === n) || null;
  }, [entries, form.company_name, initial]);
  const contactDups = useMemo(() => {
    return form.contacts.map((c, i) => {
      const name = norm(c.name), email = norm(c.email);
      if (!name && !email) return null;
      for (const e of entries) {
        if (e.id === initial?.id) continue;
        const hit = (e.contacts || []).find((x) => (email && norm(x.email) === email) || (name && norm(x.name) === name));
        if (hit) return { company: e.company_name, name: hit.name || hit.email };
      }
      const selfHit = form.contacts.findIndex((x, j) => j !== i && ((email && norm(x.email) === email) || (name && norm(x.name) === name)));
      if (selfHit !== -1 && selfHit < i) return { company: "this form (duplicate row)", name: c.name || c.email };
      return null;
    });
  }, [entries, form.contacts, initial]);
  const hasDup = !!companyDup || contactDups.some(Boolean);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (i, k, v) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const removeContact = (i) => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, idx) => idx !== i) }));
  const setAddress = (i, k, v) => setForm((f) => ({ ...f, addresses: f.addresses.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) }));
  const addAddress = () => setForm((f) => ({ ...f, addresses: [...f.addresses, emptyAddress()] }));
  const removeAddress = (i) => setForm((f) => {
    const next = f.addresses.filter((_, idx) => idx !== i);
    return { ...f, addresses: next.length ? next : [emptyAddress()] };
  });

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    if (hasDup && !dupConfirmed) {
      setDupConfirmed(true);
      toast.warning("Possible duplicate found — review the warnings, then click \"Save anyway\" to proceed.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        addresses: form.addresses.filter((a) => a.text.trim()).map(({ _uid, ...a }) => a),
        contacts: form.contacts.filter((c) => c.name || c.email || c.phone || c.phone_mobile || c.title).map(({ _uid, ...c }) => c),
      };
      if (initial?.id && targetEndpoint !== endpoint) {
        // Move to a different directory: create there, then remove here
        await api.post(targetEndpoint, payload);
        await api.delete(`${endpoint}/${initial.id}`);
        const target = DIRECTORIES.find((d) => d.endpoint === targetEndpoint);
        toast.success(`Moved “${form.company_name.trim()}” to ${target?.label || targetEndpoint}`);
        onMoved(initial.id);
        onOpenChange(false);
        return;
      }
      const res = initial?.id ? await api.put(`${endpoint}/${initial.id}`, payload) : await api.post(endpoint, payload);
      onSaved(res.data, !!initial?.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const label = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-600";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid={`${prefix}-dialog`}>
        <DialogHeader><DialogTitle>{initial?.id ? `Edit ${singular}` : `New ${singular}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {initial?.id && (
            <div className="space-y-1.5">
              <Label className={label}>Directory / tab</Label>
              <Select value={targetEndpoint} onValueChange={setTargetEndpoint}>
                <SelectTrigger className="h-9 w-56 rounded-sm" data-testid={`${prefix}-directory-select`}>
                  <span>{DIRECTORIES.find((d) => d.endpoint === targetEndpoint)?.label || "Directory"}</span>
                </SelectTrigger>
                <SelectContent>
                  {DIRECTORIES.map((d) => (
                    <SelectItem key={d.key} value={d.endpoint} data-testid={`${prefix}-directory-option-${d.key}`}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {targetEndpoint !== endpoint && (
                <p className="text-xs font-medium text-[#002FA7]" data-testid={`${prefix}-move-note`}>
                  On save, this contact moves to {DIRECTORIES.find((d) => d.endpoint === targetEndpoint)?.label}{targetIsSupplier ? " — set its status and categories below" : ""}.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className={`space-y-1.5 ${showRegionField ? "" : "md:col-span-2"}`}>
              <Label className={label}>Company name</Label>
              <Input value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} placeholder="Company name" className="h-9 rounded-sm" data-testid={`${prefix}-company-input`} autoFocus />
              {companyDup && (
                <p className="flex items-center gap-1 text-xs font-medium text-amber-600" data-testid={`${prefix}-company-dup-warning`}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> A {singular} named “{companyDup.company_name}” already exists.
                </p>
              )}
            </div>
            {showRegionField && (
              <div className="space-y-1.5">
                <Label className={label}>Abbreviation</Label>
                <Input value={form.abbreviation || ""} onChange={(e) => setField("abbreviation", e.target.value)} placeholder="e.g. NTG, EB, SYS" maxLength={20} className="h-9 rounded-sm" data-testid={`${prefix}-abbreviation-input`} />
                <p className="text-[10px] text-slate-400">Short code for this customer — used in document names like BOLs and invoices. Falls back to the full name when blank.</p>
              </div>
            )}
            <div className="space-y-1.5 md:col-span-2">
              <Label className={label}>Website</Label>
              <Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="example.com" className="h-9 rounded-sm" data-testid={`${prefix}-website-input`} />
            </div>
            {targetEndpoint === "/land-freight" && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className={label}>Shipper Portal Link</Label>
                <Input value={form.portal_url || ""} onChange={(e) => setField("portal_url", e.target.value)} placeholder="portal.example.com/login" className="h-9 rounded-sm" data-testid={`${prefix}-portal-input`} />
              </div>
            )}
            {(targetEndpoint === "/suppliers" || targetEndpoint === "/co-packers") && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className={label}>Banking Information</Label>
                <div className="grid gap-2 md:grid-cols-3">
                  <Input value={form.bank_name || ""} onChange={(e) => setField("bank_name", e.target.value)} placeholder="Bank" className="h-9 rounded-sm" data-testid={`${prefix}-bank-name-input`} />
                  <Input value={form.bank_routing || ""} onChange={(e) => setField("bank_routing", e.target.value)} placeholder="Routing Num" className="h-9 rounded-sm" data-testid={`${prefix}-bank-routing-input`} />
                  <Input value={form.bank_account || ""} onChange={(e) => setField("bank_account", e.target.value)} placeholder="Account Num" className="h-9 rounded-sm" data-testid={`${prefix}-bank-account-input`} />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className={label}>Addresses</Label>
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addAddress} data-testid={`${prefix}-add-address`}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Add address
              </Button>
            </div>
            {form.addresses.map((a, i) => (
              <div key={a._uid} className="space-y-2 rounded-sm border border-slate-200 p-2" data-testid={`${prefix}-address-${i}`}>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-[180px_1fr_auto]">
                  <Input placeholder="Label (e.g. Billing, Warehouse)" value={a.label} onChange={(e) => setAddress(i, "label", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-address-label-${i}`} />
                  <Textarea placeholder={"123 Industrial Rd\nSuite 4\nCity, ST 00000"} value={a.text} onChange={(e) => setAddress(i, "text", e.target.value)} rows={2} className="min-h-[38px] rounded-sm" data-testid={`${prefix}-address-text-${i}`} />
                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeAddress(i)} data-testid={`${prefix}-address-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input placeholder="HOO — e.g. 8:30AM – 5:00PM" value={a.hoo || ""} onChange={(e) => setAddress(i, "hoo", e.target.value)} className="h-8 w-56 rounded-sm text-xs" data-testid={`${prefix}-address-hoo-${i}`} />
                  <div className="inline-flex overflow-hidden rounded-sm border border-slate-200" data-testid={`${prefix}-address-delpu-${i}`}>
                    {[["del", "DEL"], ["pu", "PU"]].map(([v, lbl]) => (
                      <button key={v} type="button"
                        onClick={() => setAddress(i, "del_pu", a.del_pu === v ? "" : v)}
                        className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${a.del_pu === v ? "bg-[#002FA7] text-white" : "bg-white text-slate-400 hover:text-slate-700"}`}
                        data-testid={`${prefix}-address-${v}-${i}`}>{lbl}</button>
                    ))}
                  </div>
                  <span className="text-[10px] text-slate-400">Hours of operation &amp; whether freight is Delivered or Picked Up at this address</span>
                </div>
                <Textarea placeholder="Delivery Instructions — e.g. Dock 4, check in at guard shack, appointment required" value={a.delivery_instructions || ""} onChange={(e) => setAddress(i, "delivery_instructions", e.target.value)} rows={1} className="min-h-[34px] rounded-sm text-xs" data-testid={`${prefix}-address-instructions-${i}`} />
              </div>
            ))}
            <p className="text-[10px] text-slate-400">Each address can span multiple lines. If a contact has more than one address, you'll pick which one to use when creating a BOL.</p>
          </div>

          {showRegionField && (
            <div className="space-y-1.5">
              <Label className={label}>Region</Label>
              <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid={`${prefix}-region-toggle`}>
                {REGIONS.map((r) => (
                  <button key={r} type="button" onClick={() => setField("region", form.region === r ? "" : r)}
                    className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${form.region === r ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                    data-testid={`${prefix}-region-${r.toLowerCase()}`}>{r}</button>
                ))}
              </div>
              <p className="text-[10px] text-slate-400">Groups this customer on the page. Click the selected region again to clear (Unassigned).</p>
            </div>
          )}

          {showSupplierFields && (
            <>
              <div className="space-y-1.5">
                <Label className={label}>Status</Label>
                <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid={`${prefix}-status-toggle`}>
                  {[["active", "Active"], ["prospective", "Prospective"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setField("status", v)}
                      className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${form.status === v ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                      data-testid={`${prefix}-status-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={label}>Component categories supplied</Label>
                {catOptions.length === 0 ? (
                  <p className="text-xs text-slate-400">No component categories yet — add components first.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid={`${prefix}-tag-options`}>
                    {catOptions.map((t) => (
                      <button key={t} type="button" onClick={() => toggleTag(t)}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${form.tags.includes(t) ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}
                        data-testid={`${prefix}-tag-option-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className={label}>Contacts</Label>
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addContact} data-testid={`${prefix}-add-contact`}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Add contact
              </Button>
            </div>
            {form.contacts.map((c, i) => (
              <div key={c._uid}>
                <div className="grid grid-cols-1 gap-2 rounded-sm border border-slate-200 p-2 md:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]" data-testid={`${prefix}-contact-${i}`}>
                <Input placeholder="Name" value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-contact-name-${i}`} />
                <Input placeholder={roleLabel} value={c.title} onChange={(e) => setContact(i, "title", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-contact-title-${i}`} />
                <Input placeholder="Email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-contact-email-${i}`} />
                <Input placeholder="Mobile phone" value={c.phone_mobile || ""} onChange={(e) => setContact(i, "phone_mobile", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-contact-phone-mobile-${i}`} />
                <Input placeholder="Office phone" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} className="h-9 rounded-sm" data-testid={`${prefix}-contact-phone-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-600" onClick={() => removeContact(i)} disabled={form.contacts.length === 1} data-testid={`${prefix}-contact-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                </div>
                {contactDups[i] && (
                  <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600" data-testid={`${prefix}-contact-dup-warning-${i}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> “{contactDups[i].name}” already exists in {contactDups[i].company}.
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label className={label}>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Payment terms, lead times, account #, etc." className="rounded-sm" data-testid={`${prefix}-notes-input`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid={`${prefix}-cancel`}>Cancel</Button>
          <Button onClick={save} disabled={saving} className={`rounded-sm text-white ${hasDup && dupConfirmed ? "bg-amber-600 hover:bg-amber-700" : "bg-[#002FA7] hover:bg-[#002080]"}`} data-testid={`${prefix}-save`}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {hasDup && dupConfirmed ? "Save anyway" : `Save ${singular}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ContactDirectory({ title, subtitle, endpoint, prefix, singular, addLabel, roleLabel = "Title / role", supplierMode = false, regionMode = false, initialQuery = "" }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(initialQuery);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { if (initialQuery) setQ(initialQuery); }, [initialQuery]);

  useEffect(() => {
    setLoading(true);
    api.get(endpoint)
      .then((r) => setEntries(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return entries
      .filter((s) => !term ||
        s.company_name.toLowerCase().includes(term) ||
        (s.abbreviation || "").toLowerCase().includes(term) ||
        (s.tags || []).some((t) => t.toLowerCase().includes(term)) ||
        (s.contacts || []).some((c) => `${c.name} ${c.email} ${c.title}`.toLowerCase().includes(term)));
  }, [entries, q]);

  const onMoved = (id) => setEntries((prev) => prev.filter((x) => x.id !== id));

  const onSaved = (s, isEdit) => {
    setEntries((prev) => {
      const next = isEdit ? prev.map((x) => (x.id === s.id ? s : x)) : [...prev, s];
      return next.sort((a, b) => a.company_name.localeCompare(b.company_name));
    });
    toast.success(isEdit ? `${singular[0].toUpperCase()}${singular.slice(1)} updated` : `${singular[0].toUpperCase()}${singular.slice(1)} added`);
  };

  const copyEmails = async () => {
    const emails = Array.from(new Set(filtered.flatMap((s) => (s.contacts || []).filter((c) => !c.suppress_email).map((c) => (c.email || "").trim()).filter(Boolean))));
    if (!emails.length) { toast.info("No email addresses to copy"); return; }
    const text = emails.join("; ");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(`Copied ${emails.length} email address${emails.length === 1 ? "" : "es"}`);
  };

  const del = async () => {
    try {
      await api.delete(`${endpoint}/${toDelete.id}`);
      setEntries((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success(`${singular[0].toUpperCase()}${singular.slice(1)} deleted`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
    setToDelete(null);
  };

  const toggleSuppress = async (s, i) => {
    const contacts = (s.contacts || []).map((c, idx) => (idx === i ? { ...c, suppress_email: !c.suppress_email } : c));
    try {
      const res = await api.put(`${endpoint}/${s.id}`, { ...s, contacts });
      setEntries((prev) => prev.map((x) => (x.id === s.id ? res.data : x)));
      const c = contacts[i];
      toast.success(c.suppress_email
        ? `${c.name || c.email || "Contact"} muted — excluded from Copy emails and freight quote emails`
        : `${c.name || c.email || "Contact"} unmuted — included again`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const POC_ENDPOINTS = ["/suppliers", "/co-packers", "/customers", "/distributors"];
  const pocEnabled = POC_ENDPOINTS.includes(endpoint);
  const toggleShipPoc = async (s, i, addressId) => {
    const contacts = (s.contacts || []).map((c, idx) => {
      if (idx !== i) return c;
      let ids = c.poc_address_ids || [];
      if (!ids.length && c.shipment_poc) ids = (s.addresses || []).map((a) => a.id); // legacy "all locations"
      ids = ids.includes(addressId) ? ids.filter((x) => x !== addressId) : [...ids, addressId];
      return { ...c, poc_address_ids: ids, shipment_poc: ids.length > 0 };
    });
    try {
      const res = await api.put(`${endpoint}/${s.id}`, { ...s, contacts });
      setEntries((prev) => prev.map((x) => (x.id === s.id ? res.data : x)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-8 md:px-10 md:py-12" data-testid={`${prefix}s-page`}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {endpoint === "/land-freight" && (
            <Button variant="outline" onClick={copyEmails} className="h-9 rounded-sm border-slate-200 text-slate-600 hover:text-[#002FA7]" data-testid="copy-emails-btn" title="Copy every contact email on this page">
              <Copy className="mr-1 h-4 w-4" /> Copy emails
            </Button>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-56 rounded-sm pl-8" data-testid={`${prefix}-search`} />
          </div>
          <Button onClick={() => setDialog({ open: true, initial: null })} className="h-9 rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`add-${prefix}-btn`}>
            <Plus className="mr-1 h-4 w-4" /> {addLabel}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-sm border border-dashed border-slate-300 py-20 text-center" data-testid={`${prefix}s-empty`}>
          <Building2 className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">{q ? "No matches for your search." : `No ${title.toLowerCase()} yet. Add your first one.`}</p>
        </div>
      ) : (() => {
          const renderCard = (s) => (
            <div key={s.id} className="flex flex-col rounded-sm border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`${prefix}-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Building2 className="h-5 w-5 shrink-0 text-[#002FA7]" />
                  <h3 className="truncate font-heading text-lg font-semibold text-slate-900">{s.company_name}</h3>
                  {s.abbreviation && (
                    <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-slate-600" data-testid={`${prefix}-abbrev-badge-${s.id}`}>{s.abbreviation}</span>
                  )}
                  {supplierMode && <StatusBadge status={s.status || "active"} />}
                  {regionMode && s.region && (
                    <span className="rounded-full bg-[#002FA7]/8 px-2 py-0.5 text-[10px] font-semibold text-[#002FA7]" style={{ backgroundColor: "rgba(0,47,167,0.08)" }} data-testid={`${prefix}-region-badge-${s.id}`}>{s.region}</span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {s.portal_url && (
                    <a href={webHref(s.portal_url)} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 rounded-sm border border-[#002FA7]/30 px-2 py-1 text-[11px] font-semibold text-[#002FA7] hover:bg-[#002FA7]/5"
                      data-testid={`portal-${prefix}-${s.id}`} title="Open shipper portal">
                      Portal <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <button onClick={() => setDialog({ open: true, initial: s })} className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#002FA7]" data-testid={`edit-${prefix}-${s.id}`} title="Edit"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setToDelete(s)} className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-${prefix}-${s.id}`} title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {s.website && (
                <a href={webHref(s.website)} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1.5 truncate text-sm text-[#002FA7] hover:underline">
                  <Globe className="h-3.5 w-3.5 shrink-0" /> {s.website}
                </a>
              )}
              {(s.addresses || []).map((a, i) => (
                <div key={a.id || i} className="mt-1" data-testid={`${prefix}-card-address-${s.id}-${i}`}>
                  <p className="whitespace-pre-line text-sm text-slate-500">
                    {a.label && <span className="font-semibold text-slate-600">{a.label}: </span>}{a.text}
                  </p>
                  {(a.hoo || a.del_pu) && (
                    <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                      {a.del_pu && <span className="rounded-sm bg-[#002FA7]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#002FA7]" data-testid={`${prefix}-card-delpu-${s.id}-${i}`}>{a.del_pu === "del" ? "DEL" : "PU"}</span>}
                      {a.hoo && <span data-testid={`${prefix}-card-hoo-${s.id}-${i}`}><span className="font-semibold text-slate-600">HOO:</span> {a.hoo}</span>}
                    </p>
                  )}
                  {a.delivery_instructions && (
                    <p className="mt-0.5 whitespace-pre-line text-[11px] italic text-slate-500" data-testid={`${prefix}-card-instructions-${s.id}-${i}`}>
                      <span className="font-semibold not-italic text-slate-600">Delivery Instructions:</span> {a.delivery_instructions}
                    </p>
                  )}
                </div>
              ))}
              {(s.bank_name || s.bank_routing || s.bank_account) && (
                <div className="group/bank mt-1.5 flex items-start gap-1.5 text-[11px] text-slate-500" data-testid={`${prefix}-card-banking-${s.id}`}>
                  <Landmark className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                  <p>
                    {s.bank_name && <span className="font-semibold text-slate-600">{s.bank_name}</span>}
                    {s.bank_routing && <span>{s.bank_name ? " · " : ""}Routing {s.bank_routing}</span>}
                    {s.bank_account && <span>{(s.bank_name || s.bank_routing) ? " · " : ""}Acct {s.bank_account}</span>}
                  </p>
                  <button type="button" title="Copy banking details"
                    onClick={() => {
                      const lines = [];
                      if (s.bank_name) lines.push(`Bank: ${s.bank_name}`);
                      if (s.bank_routing) lines.push(`Routing: ${s.bank_routing}`);
                      if (s.bank_account) lines.push(`Account: ${s.bank_account}`);
                      navigator.clipboard.writeText(lines.join("\n"))
                        .then(() => toast.success(`Banking details for ${s.company_name} copied`))
                        .catch(() => toast.error("Could not copy to clipboard"));
                    }}
                    className="rounded-sm p-0.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-[#002FA7] group-hover/bank:text-slate-400"
                    data-testid={`copy-banking-${prefix}-${s.id}`}>
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
              {supplierMode && (s.tags || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`${prefix}-card-tags-${s.id}`}>
                  {s.tags.map((t) => (
                    <span key={t} className="rounded-full bg-[#002FA7]/8 px-2 py-0.5 text-[10px] font-semibold text-[#002FA7]" style={{ backgroundColor: "rgba(0,47,167,0.08)" }}>{t}</span>
                  ))}
                </div>
              )}

              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                {(s.contacts || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No contacts yet</p>
                ) : (
                  s.contacts.map((c, i) => (
                    <div key={`${c.name || "c"}-${c.email || ""}-${i}`} className={`group/contact text-sm ${c.suppress_email ? "opacity-60" : ""}`} data-testid={`${prefix}-card-contact-${s.id}-${i}`}>
                      <div className="flex items-center gap-1.5 font-medium text-slate-800">
                        <User className="h-3.5 w-3.5 shrink-0 text-slate-400" /> {c.name || "—"}
                        {c.title && <span className="text-xs font-normal text-slate-400">· {c.title}</span>}
                        {c.suppress_email && (
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500" data-testid={`${prefix}-muted-badge-${s.id}-${i}`}>Muted</span>
                        )}
                        {pocEnabled && (() => {
                          const legacyAll = c.shipment_poc && !(c.poc_address_ids || []).length;
                          const ids = legacyAll ? (s.addresses || []).map((a) => a.id) : (c.poc_address_ids || []);
                          const isPoc = ids.length > 0;
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button type="button"
                                  className={`ml-auto rounded-sm p-1 transition-colors ${isPoc ? "bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7]/20" : "text-slate-300 hover:bg-slate-100 hover:text-[#002FA7] group-hover/contact:text-slate-400"}`}
                                  title={isPoc ? "Shipment POC — click to manage locations" : "Set as Shipment POC for one or more locations"}
                                  data-testid={`${prefix}-poc-contact-${s.id}-${i}`}>
                                  <Truck className="h-3.5 w-3.5" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="end" className="w-72 rounded-sm p-3">
                                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Shipment POC — {c.name || c.email || "Contact"}</p>
                                <p className="mb-2 text-[11px] text-slate-400">Included on freight quote emails for the checked locations.</p>
                                {(s.addresses || []).length === 0 ? (
                                  <p className="text-xs text-slate-400">No addresses on this {singular} yet — add one first.</p>
                                ) : (s.addresses || []).map((a, ai) => (
                                  <label key={a.id || ai} className="flex cursor-pointer items-start gap-2 rounded-sm px-1 py-1.5 text-sm hover:bg-slate-50" data-testid={`${prefix}-poc-addr-${s.id}-${i}-${ai}`}>
                                    <Checkbox checked={ids.includes(a.id)} onCheckedChange={() => toggleShipPoc(s, i, a.id)} className="mt-0.5" data-testid={`${prefix}-poc-addr-check-${s.id}-${i}-${ai}`} />
                                    <span className="min-w-0">
                                      {a.label && <span className="block text-xs font-semibold text-slate-700">{a.label}</span>}
                                      <span className="block truncate text-xs text-slate-500">{(a.text || "").split("\n")[0]}</span>
                                    </span>
                                  </label>
                                ))}
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                        {endpoint === "/land-freight" && (
                          <button type="button" onClick={() => toggleSuppress(s, i)}
                            className={`ml-auto rounded-sm p-1 transition-colors ${c.suppress_email ? "bg-red-50 text-red-500 hover:bg-red-100" : "text-slate-300 hover:bg-slate-100 hover:text-red-500 group-hover/contact:text-slate-400"}`}
                            title={c.suppress_email ? "Muted — excluded from Copy emails and freight quote emails. Click to include again." : "Mute — exclude from Copy emails and freight quote emails"}
                            data-testid={`${prefix}-mute-contact-${s.id}-${i}`}>
                            <MailX className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button type="button" onClick={() => copyContact({ ...c, company: s.company_name })}
                          className={`${endpoint === "/land-freight" || pocEnabled ? "" : "ml-auto "}rounded-sm p-1 text-slate-300 transition-colors hover:bg-slate-100 hover:text-[#002FA7] group-hover/contact:text-slate-400`}
                          title="Copy contact" data-testid={`${prefix}-copy-contact-${s.id}-${i}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="ml-5 space-y-0.5 text-xs text-slate-500">
                        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Mail className="h-3 w-3" /> {c.email}</a>}
                        {c.phone_mobile && <a href={`tel:${c.phone_mobile}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Smartphone className="h-3 w-3" /> {c.phone_mobile} <span className="text-[10px] text-slate-400">Mobile</span></a>}
                        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#002FA7]"><Phone className="h-3 w-3" /> {c.phone} <span className="text-[10px] text-slate-400">Office</span></a>}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {s.notes && <p className="mt-3 border-t border-slate-100 pt-3 text-xs italic text-slate-500">{s.notes}</p>}
            </div>
          );
          if (regionMode) {
            const regionSections = [
              { key: "US", label: "US" },
              { key: "Europe", label: "Europe" },
              { key: "UK", label: "UK" },
              { key: "", label: "Unassigned" },
            ];
            return (
              <div className="space-y-10">
                {regionSections.map((sec) => {
                  const group = filtered.filter((s) => (REGIONS.includes(s.region) ? s.region : "") === sec.key);
                  if (sec.key === "" && group.length === 0) return null;
                  return (
                    <section key={sec.label} data-testid={`${prefix}-region-section-${sec.label.toLowerCase()}`}>
                      <div className="mb-3 flex items-center gap-2 border-b-2 border-[#002FA7] pb-2">
                        <MapPin className="h-5 w-5 text-[#002FA7]" strokeWidth={1.5} />
                        <span className="font-heading text-lg font-bold tracking-tight text-slate-900">{sec.label}</span>
                        <span className="text-sm text-slate-400">{group.length}</span>
                      </div>
                      {group.length === 0 ? (
                        <p className="py-4 text-sm text-slate-400" data-testid={`${prefix}-region-empty-${sec.label.toLowerCase()}`}>No {sec.label} customers yet.</p>
                      ) : (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{group.map(renderCard)}</div>
                      )}
                    </section>
                  );
                })}
              </div>
            );
          }
          if (!supplierMode) {
            return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{filtered.map(renderCard)}</div>;
          }
          const sections = [
            { key: "active", label: "Active Suppliers", icon: CheckCircle2 },
            { key: "prospective", label: "Prospective Suppliers", icon: Compass },
          ];
          return (
            <div className="space-y-10">
              {sections.map((sec) => {
                const group = filtered.filter((s) => (s.status || "active") === sec.key);
                return (
                  <section key={sec.key} data-testid={`${prefix}-section-${sec.key}`}>
                    <div className="mb-3 flex items-center gap-2 border-b-2 border-[#002FA7] pb-2">
                      <sec.icon className="h-5 w-5 text-[#002FA7]" strokeWidth={1.5} />
                      <span className="font-heading text-lg font-bold tracking-tight text-slate-900">{sec.label}</span>
                      <span className="text-sm text-slate-400">{group.length}</span>
                    </div>
                    {group.length === 0 ? (
                      <p className="py-4 text-sm text-slate-400" data-testid={`${prefix}-section-empty-${sec.key}`}>No {sec.label.toLowerCase()} yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{group.map(renderCard)}</div>
                    )}
                  </section>
                );
              })}
            </div>
          );
        })()}

      <EntryDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} onSaved={onSaved} onMoved={onMoved} endpoint={endpoint} prefix={prefix} singular={singular} roleLabel={roleLabel} supplierMode={supplierMode} regionMode={regionMode} entries={entries} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {singular}?</AlertDialogTitle>
            <AlertDialogDescription>This removes “{toDelete?.company_name}” and its contacts. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-${prefix}`}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
